/**
 * useCarouselScroll
 * ─────────────────────────────────────────────────────────────────────
 * Logica condivisa dei caroselli orizzontali (news, banner, opere d'arte).
 *
 * Lo scorrimento col dito NON e' simulato in JavaScript: e' lo scroll
 * nativo del browser (overflow-x + scroll-snap). Su iOS/Android lo swipe
 * e' quindi quello di sistema, con inerzia e rimbalzo, e non dipende da
 * nostri handler di touch.
 *
 * Il JavaScript qui serve solo per:
 *   - frecce e puntini (scrollTo/scrollBy con behavior smooth)
 *   - trascinamento col MOUSE su desktop (il dito non entra mai qui)
 *
 * USO:
 *   const { viewport, track, totalDots, currentDotIndex, canScrollLeft,
 *           canScrollRight, scrollPrev, scrollNext, goToDot, measure }
 *     = useCarouselScroll({ itemCount: () => items.length, cardSelector: '.news-card' })
 *
 *   observeResize (default true): rimisura da sola quando il track cambia
 *   dimensione. Con false le misure si rifanno solo al mount, al resize della
 *   finestra, al cambio del numero di elementi e quando si chiama measure().
 *
 *   <div class="carousel-viewport" ref="viewport">
 *     <div class="carousel-track" ref="track"> ...card... </div>
 *   </div>
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

export function useCarouselScroll({ itemCount, cardSelector, observeResize = true }) {
  const viewport = ref(null)   // contenitore che scrolla davvero
  const track = ref(null)      // riga flex con le card

  const cardWidth = ref(0)
  const gap = ref(0)
  const visibleCards = ref(1)
  const scrollLeft = ref(0)
  const maxScroll = ref(0)

  /** Passo di una card (larghezza + gap) */
  const step = computed(() => Math.max(1, cardWidth.value + gap.value))

  /** Quanto si sposta lo scroll passando da un puntino al successivo */
  const pageSize = computed(() => step.value * visibleCards.value)

  // Finche' non abbiamo misurato, visibleCards vale 1: senza questa guardia
  // al primo paint comparirebbe un puntino per card (es. 6) che un istante
  // dopo diventa 2. Meglio non disegnarli affatto finche' non sappiamo.
  const isMeasured = ref(false)

  /**
   * Numero di puntini.
   *
   * NON si ricava da ceil(card / cardVisibili): quel conto passa per la
   * larghezza della card, che e' frazionaria (es. 458,67px), e un errore di
   * mezzo pixel fa comparire un puntino fantasma - un puntino in piu' di
   * quante siano le posizioni in cui il carosello puo' davvero fermarsi.
   *
   * Lo ricaviamo invece dallo scroll REALE: quante pagine ci stanno dentro
   * maxScroll, piu' quella di partenza. Cosi' i puntini sono per costruzione
   * tanti quante le posizioni raggiungibili, e nessuno di loro e' inerte.
   */
  const totalDots = computed(() => {
    if (!isMeasured.value || !itemCount() || maxScroll.value <= 0) return 0
    return Math.ceil(maxScroll.value / pageSize.value) + 1
  })

  /**
   * Puntino attivo.
   *
   * ATTENZIONE, qui c'e' un tranello: l'ultima "pagina" quasi mai comincia a
   * un multiplo esatto di pageSize, perche' lo scroll si ferma prima
   * (maxScroll). Es. 5 card, 3 visibili: i puntini sono 2, ma il secondo
   * vorrebbe scrollare a 3 card mentre il massimo raggiungibile e' 2 -> con
   * un semplice floor(scroll / pageSize) l'ultimo puntino non si accende MAI.
   * Percio': se siamo in fondo, il puntino attivo e' per definizione l'ultimo.
   */
  const currentDotIndex = computed(() => {
    const last = Math.max(0, totalDots.value - 1)
    if (maxScroll.value <= 0) return 0
    if (scrollLeft.value >= maxScroll.value - 1) return last
    const dot = Math.round(scrollLeft.value / pageSize.value)
    return Math.max(0, Math.min(dot, last))
  })

  // Le frecce si basano sullo scroll reale, non sull'indice: cosi' restano
  // corrette anche con card di larghezza diversa o scroll a meta' strada.
  const canScrollLeft = computed(() => scrollLeft.value > 1)
  const canScrollRight = computed(() => scrollLeft.value < maxScroll.value - 1)

  // ── Misure ──────────────────────────────────────────────────────────
  function measure() {
    const el = viewport.value
    const tr = track.value
    if (!el || !tr) return

    // Lo scroll e' sempre leggibile, anche prima che le card abbiano dimensioni.
    maxScroll.value = Math.max(0, el.scrollWidth - el.clientWidth)
    scrollLeft.value = el.scrollLeft

    const cardEl = tr.querySelector(cardSelector)
    const width = cardEl ? Math.round(cardEl.getBoundingClientRect().width) : 0

    // Card ancora senza larghezza (immagini/font in arrivo): NON ricalcoliamo
    // il passo con dati falsi, terremmo misure sbagliate. Ci ripensa il
    // ResizeObserver appena le card prendono forma.
    if (width <= 0) return
    cardWidth.value = width

    const cs = window.getComputedStyle(tr)
    const gapPx = parseFloat(cs.columnGap || cs.gap || '0')
    gap.value = Number.isNaN(gapPx) ? 0 : gapPx

    const perView = Math.floor((el.clientWidth + gap.value) / step.value)
    visibleCards.value = Math.max(1, Math.min(perView, itemCount() || 1))
    isMeasured.value = true
  }

  // Se cambia il numero di elementi (dati che arrivano dall'API, filtri),
  // le misure vanno rifatte: cambia scrollWidth e quindi puntini e frecce.
  watch(() => itemCount(), async () => {
    await nextTick()
    measure()
  })

  // ── Sincronizzazione con lo scroll del browser ──────────────────────
  let rafId = null
  function onScroll() {
    if (rafId) return
    rafId = requestAnimationFrame(() => {
      rafId = null
      const el = viewport.value
      if (!el) return
      scrollLeft.value = el.scrollLeft
      maxScroll.value = Math.max(0, el.scrollWidth - el.clientWidth)
    })
  }

  // ── Navigazione (frecce e puntini) ──────────────────────────────────
  function scrollToPosition(left) {
    const el = viewport.value
    if (!el) return
    el.scrollTo({ left: Math.max(0, Math.min(left, maxScroll.value)), behavior: 'smooth' })
  }

  function scrollPrev() {
    scrollToPosition(scrollLeft.value - pageSize.value)
  }

  function scrollNext() {
    scrollToPosition(scrollLeft.value + pageSize.value)
  }

  function goToDot(dotIndex) {
    // L'ultimo puntino porta in fondo: la sua pagina e' piu' corta delle altre
    // (vedi la nota su currentDotIndex), quindi non e' a dotIndex * pageSize.
    if (dotIndex >= totalDots.value - 1) {
      scrollToPosition(maxScroll.value)
      return
    }
    scrollToPosition(dotIndex * pageSize.value)
  }

  // ── Trascinamento col mouse (solo desktop) ──────────────────────────
  // Il dito non passa di qui: pointerType 'touch' viene ignorato e lo
  // swipe resta interamente al browser.
  const isDragging = ref(false)
  let dragStartX = 0
  let dragStartScroll = 0
  let dragMoved = false

  const DRAG_THRESHOLD = 5   // px oltre i quali il gesto e' un trascinamento, non un click

  function onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return
    const el = viewport.value
    if (!el) return

    isDragging.value = true
    dragMoved = false
    dragStartX = event.clientX
    dragStartScroll = el.scrollLeft
    // ATTENZIONE: NIENTE setPointerCapture qui. Con la cattura attiva il
    // browser ri-targetta mousedown/mouseup (e quindi il click) sul viewport:
    // il click non arriverebbe mai all'<a> della card e i link morirebbero
    // a ogni singolo click, non solo dopo un trascinamento.
    // La cattura si prende dopo, e solo se il gesto diventa davvero un drag.
  }

  function onPointerMove(event) {
    if (!isDragging.value) return
    const el = viewport.value
    if (!el) return

    const dx = event.clientX - dragStartX
    if (Math.abs(dx) > DRAG_THRESHOLD && !dragMoved) {
      dragMoved = true
      // Ora sì: da qui in poi è un trascinamento, e la cattura serve per non
      // perdere il gesto se il mouse esce dal carosello.
      try { el.setPointerCapture(event.pointerId) } catch { /* pointer già rilasciato */ }
    }
    if (!dragMoved) return
    el.scrollLeft = dragStartScroll - dx
  }

  function onPointerUp(event) {
    if (!isDragging.value) return
    const el = viewport.value
    isDragging.value = false

    if (el && el.hasPointerCapture?.(event.pointerId)) {
      el.releasePointerCapture(event.pointerId)
    }

    // Se abbiamo trascinato, il click successivo aprirebbe la card sotto il
    // cursore: lo blocchiamo una volta sola, in fase di cattura.
    if (dragMoved) {
      const swallow = (e) => { e.preventDefault(); e.stopPropagation() }
      el?.addEventListener('click', swallow, { capture: true, once: true })
      // Se il click non arriva (es. mouse uscito dalla pagina) il listener
      // resterebbe appeso e mangerebbe il PRIMO click buono successivo:
      // lo togliamo dopo un frame, non con un timeout a 0 (che può scattare
      // prima del click e vanificare la soppressione).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => el?.removeEventListener('click', swallow, { capture: true }))
      })
    }

    // Aggancio alla card piu' vicina: lo scroll-snap torna attivo appena
    // cade la classe .is-dragging, ma lo chiediamo anche esplicitamente
    // cosi' il risultato e' lo stesso su tutti i browser.
    // Solo dopo un trascinamento vero: su un click secco il carosello non
    // deve muoversi di un pixel.
    if (el && dragMoved) {
      scrollToPosition(Math.round(el.scrollLeft / step.value) * step.value)
    }
  }

  // ── Ciclo di vita ───────────────────────────────────────────────────
  //
  // Il contenitore puo' NON esistere al mount: le sezioni sono spesso dentro
  // un v-if legato ai dati dell'API (es. i banner, con v-if="shouldShowBanners"),
  // quindi compaiono solo quando la risposta arriva. Percio' non agganciamo i
  // listener nel mount, ma osserviamo il ref: appena l'elemento c'e', misuriamo
  // e ci agganciamo; se sparisce, ci stacchiamo.
  let resizeObserver = null
  let attachedEl = null

  function detach() {
    if (attachedEl) attachedEl.removeEventListener('scroll', onScroll)
    if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null }
    attachedEl = null
  }

  watch(viewport, (el) => {
    detach()
    if (!el) return

    el.addEventListener('scroll', onScroll, { passive: true })
    attachedEl = el

    // Le misure vengono PRIMA di tutto il resto: sono quelle da cui dipendono
    // frecce e puntini, e non devono poter saltare se qualcos'altro fallisce.
    measure()

    // Le card cambiano dimensione anche senza resize della finestra (immagini
    // che arrivano, font che caricano): rimisuriamo quando cambia il track.
    // observeResize: false quando il contenitore cambia larghezza con
    // un'animazione (i pannelli della landing): l'osservatore scatterebbe a ogni
    // fotogramma, e rimisure e ridisegno dei puntini rendevano l'animazione a
    // scatti. Chi lo spegne richiama measure() a fine animazione. (2026-09-17)
    const RO = window.ResizeObserver
    if (observeResize && RO && track.value) {
      resizeObserver = new RO(measure)
      resizeObserver.observe(track.value)
    }
  }, { immediate: true, flush: 'post' })

  onMounted(() => {
    window.addEventListener('resize', measure)
  })

  onUnmounted(() => {
    detach()
    window.removeEventListener('resize', measure)
    if (rafId) cancelAnimationFrame(rafId)
  })

  return {
    viewport,
    track,
    isDragging,
    visibleCards,
    totalDots,
    currentDotIndex,
    canScrollLeft,
    canScrollRight,
    scrollPrev,
    scrollNext,
    goToDot,
    measure,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  }
}
